import { defineStore } from 'pinia';
import { getCurrentProfile } from '@/js/core/utils/profile-api';
import {
  DEFAULT_TIMEZONE,
  getDateInTimezone,
  getTimezoneLabel,
  getTimezoneOffsetDifferenceHours,
  normalizeTimezone,
} from '@/js/core/utils/timezone';

let hydrationPromise = null;
let clockInterval = null;

export const useTimezoneStore = defineStore('timezone', {
  state: () => ({
    timezone: DEFAULT_TIMEZONE,
    defaultTimezone: DEFAULT_TIMEZONE,
    useCustomTimezone: false,
    timezoneOption: 'gsd',
    timeDifference: 0,
    clock: Date.now(),
    hydrated: false,
    isHydrating: false,
  }),
  getters: {
    effectiveTimezone(state) {
      return state.useCustomTimezone ? state.timezone : state.defaultTimezone;
    },
    timezoneLabel(state) {
      return getTimezoneLabel(state.timezone);
    },
    effectiveTimezoneLabel(state) {
      return getTimezoneLabel(state.useCustomTimezone ? state.timezone : state.defaultTimezone);
    },
    currentTime(state) {
      const timezone = state.useCustomTimezone ? state.timezone : state.defaultTimezone;

      return getDateInTimezone(state.clock, timezone);
    },
  },
  actions: {
    hydrateFromProfile(profile) {
      const settings = profile?.timezone || profile || {};
      const defaultTimezone = normalizeTimezone(settings.default_timezone, DEFAULT_TIMEZONE);
      const timezone = normalizeTimezone(settings.timezone, defaultTimezone);

      this.defaultTimezone = defaultTimezone;
      this.timezone = timezone;
      this.useCustomTimezone = settings.use_custom_timezone === true;
      this.timezoneOption = this.useCustomTimezone ? 'user' : 'gsd';
      this.timeDifference = getTimezoneOffsetDifferenceHours(
        this.clock,
        this.effectiveTimezone,
        this.defaultTimezone
      );
      this.hydrated = true;
    },

    async hydrateFromCurrentProfile({ force = false } = {}) {
      if (this.hydrated && !force) {
        return this.effectiveTimezone;
      }

      if (hydrationPromise && !force) {
        return hydrationPromise;
      }

      this.isHydrating = true;
      hydrationPromise = getCurrentProfile()
        .then(response => {
          this.hydrateFromProfile(response?.data || null);
          return this.effectiveTimezone;
        })
        .catch(() => {
          this.hydrated = true;
          return this.effectiveTimezone;
        })
        .finally(() => {
          this.isHydrating = false;
          hydrationPromise = null;
        });

      return hydrationPromise;
    },

    setTimezone(timezone, { enableCustom = true } = {}) {
      this.timezone = normalizeTimezone(timezone, this.defaultTimezone);
      this.useCustomTimezone = enableCustom;
      this.timezoneOption = this.useCustomTimezone ? 'user' : 'gsd';
      this.timeDifference = getTimezoneOffsetDifferenceHours(
        this.clock,
        this.effectiveTimezone,
        this.defaultTimezone
      );
    },

    setUseCustomTimezone(useCustomTimezone) {
      this.useCustomTimezone = Boolean(useCustomTimezone);
      this.timezoneOption = this.useCustomTimezone ? 'user' : 'gsd';
      this.timeDifference = getTimezoneOffsetDifferenceHours(
        this.clock,
        this.effectiveTimezone,
        this.defaultTimezone
      );
    },

    setOption(option) {
      this.setUseCustomTimezone(option === 'user');
    },

    reset() {
      this.timezone = this.defaultTimezone;
      this.useCustomTimezone = false;
      this.timezoneOption = 'gsd';
      this.timeDifference = 0;
      this.hydrated = false;
    },

    startClock() {
      if (clockInterval) return;

      clockInterval = setInterval(() => {
        this.clock = Date.now();
        this.timeDifference = getTimezoneOffsetDifferenceHours(
          this.clock,
          this.effectiveTimezone,
          this.defaultTimezone
        );
      }, 20000);
    },
  },
});
